import { cn } from '@recipes/ui';
import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useDocumentTitle } from '@/lib/hooks/use-document-title';

export interface PageHeaderProps {
  title: string;
  description?: ReactNode;
  /** Buttons rendered on the right (wrap on small screens). */
  actions?: ReactNode;
  /** Breadcrumb / back link rendered above the title. */
  eyebrow?: ReactNode;
  className?: string;
  /** Skip focusing the heading on mount (e.g. when a dialog opens immediately). */
  autoFocus?: boolean;
  /** Extra text for the browser tab; defaults to `title`. */
  documentTitle?: string;
}

/**
 * Page title block used by every screen. It moves keyboard focus to the heading whenever
 * the route changes so screen-reader users hear the new page (WCAG 2.4.3) and it sets
 * the document title.
 */
export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
  className,
  autoFocus = true,
  documentTitle,
}: PageHeaderProps) {
  const heading = useRef<HTMLHeadingElement>(null);
  const { pathname } = useLocation();
  useDocumentTitle(documentTitle ?? title);
  useEffect(() => {
    if (!autoFocus) return;
    const active = document.activeElement;
    // Do not steal focus from a control the user is already using (e.g. a search box).
    if (active && active !== document.body && active.closest('input, textarea, [role=dialog]'))
      return;
    heading.current?.focus({ preventScroll: false });
  }, [pathname, autoFocus]);

  return (
    <header
      className={cn(
        'mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between',
        className,
      )}
    >
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-sm text-muted-foreground">{eyebrow}</div>}
        <h1
          ref={heading}
          tabIndex={-1}
          className="break-words text-2xl font-bold tracking-tight outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm md:text-3xl"
        >
          {title}
        </h1>
        {description && <div className="mt-1 text-sm text-muted-foreground">{description}</div>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}
